import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { CustomMovement } from 'types';

import { getPartnerCustomMovements } from '../api/getPartnerCustomMovements';

type UseGetPartnerCustomMovementsProps = {
  partnerId: string;
  page?: number;
  rowsPerPage?: number;
};

export const useGetPartnerCustomMovements = ({
  partnerId,
  page = 1,
  rowsPerPage = 10,
}: UseGetPartnerCustomMovementsProps) => {
  const [customMovements, setCustomMovements] = useState<CustomMovement[]>([]);
  const [customMovementsTotal, setCustomMovementsTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchNotes = useCallback(
    async (controller: AbortController) => {
      if (!partnerId) return;

      try {
        setIsLoading(true);

        const fetchedData = await store.dispatch(
          getPartnerCustomMovements({
            partnerId,
            params: {
              page,
              items: rowsPerPage,
            },
            signal: controller.signal,
          })
        );

        setCustomMovements(fetchedData?.customMovements || []);
        setCustomMovementsTotal(fetchedData?.total || 0);

        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    [page, rowsPerPage, partnerId]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchNotes(controller);

    return () => controller.abort();
  }, [fetchNotes]);

  return {
    data: { customMovements, customMovementsTotal },
    isLoading,
    isError,
  };
};
